import {
  CreditCard,
  GraduationCap,
  Loader2,
  Sparkles,
  TrendingUp,
  type LucideIcon,
} from 'lucide-react';
import { useState } from 'react';

import { useGet90PercentFullClasses } from '~entities/dashboard/api/get-90-percent-full-classes';
import { useGetTrialConversion } from '~entities/dashboard/api/get-trial-conversion';
import { useGetAllTrialStudents } from '~entities/student/api/get-all-trial-students';
import { useGetExpiringCards } from '~entities/studios/api/get-expiring-cards';
import { useCurrentStudio } from '~entities/studios/model/studio-store';
import { useCurrentUserRole } from '~entities/users/hooks/use-current-user-role';

import { getTrialWeekDateRange } from '~features/new-dashboard/helpers/trial-week-date-range';
import { formatTrialConversionRate } from '~features/new-dashboard/helpers/format-trial-conversion';
import { TrialConversionSummary } from '~features/new-dashboard/ui/trial-conversion-summary';

import { cn } from '~shared/shadcn/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '~shared/shadcn/ui/dialog';

interface UrgentIssuesBannerProps {
  sessionId: string;
}

type MetricColor = 'yellow' | 'blue' | 'emerald' | 'red';

const textColorClass: Record<MetricColor, string> = {
  yellow: 'text-yellow-700',
  blue: 'text-blue-700',
  emerald: 'text-emerald-700',
  red: 'text-red-700',
};

const bgColorClass: Record<MetricColor, string> = {
  yellow: 'bg-yellow-50',
  blue: 'bg-blue-50',
  emerald: 'bg-emerald-50',
  red: 'bg-red-50',
};

const borderColorClass: Record<MetricColor, string> = {
  yellow: 'border-yellow-700 hover:border-yellow-700',
  blue: 'border-blue-700 hover:border-blue-700',
  emerald: 'border-emerald-700 hover:border-emerald-700',
  red: 'border-red-700 hover:border-red-700',
};

interface MetricItem {
  id: string;
  color: MetricColor;
  icon: LucideIcon;
  label: string;
  description: string;
  mobileValue: React.ReactNode;
  modalValue: React.ReactNode;
  desktopLabel: React.ReactNode;
  isLoading?: boolean;
}

const CompactMetric = ({
  color,
  icon: Icon,
  value,
  label,
  isLoading,
  onClick,
}: {
  color: MetricColor;
  icon: LucideIcon;
  value: React.ReactNode;
  label: string;
  isLoading?: boolean;
  onClick: () => void;
}) => (
  <button
    type='button'
    className={cn(
      'flex min-w-0 flex-1 flex-col items-center justify-center gap-1 rounded-lg px-1 py-1.5',
      'transition-colors active:bg-white/60',
    )}
    aria-label={label}
    onClick={onClick}
  >
    <Icon className={cn('size-4 shrink-0', textColorClass[color])} strokeWidth={2} />
    <div
      className={cn(
        'text-sm font-semibold tabular-nums leading-none',
        textColorClass[color],
      )}
    >
      {isLoading ? <Loader2 className='size-3.5 animate-spin' /> : value}
    </div>
  </button>
);

const DesktopPill = ({
  color,
  icon: Icon,
  children,
  isLoading,
}: {
  color: MetricColor;
  icon: LucideIcon;
  children: React.ReactNode;
  isLoading?: boolean;
}) => (
  <div
    className={cn(
      'group flex w-auto items-center justify-center gap-2.5 rounded-full border-2 bg-white/80 px-3.5 py-2 text-center transition-all duration-150',
      'hover:scale-[1.02] hover:bg-white hover:shadow-md active:scale-[0.98]',
      borderColorClass[color],
    )}
  >
    <Icon className={cn('size-4 shrink-0', textColorClass[color])} strokeWidth={2} />
    <div className={cn('text-sm font-semibold', textColorClass[color])}>
      {isLoading ? <Loader2 className='mx-auto size-4 animate-spin' /> : children}
    </div>
  </div>
);

export const UrgentIssuesBanner = ({ sessionId }: UrgentIssuesBannerProps) => {
  const [selectedMetricId, setSelectedMetricId] = useState<string | null>(null);
  const studioTimezone = useCurrentStudio()?.timezone;

  const { startDate: formattedFirstDayOfWeek, endDate: formattedLastDayOfWeek } =
    getTrialWeekDateRange(new Date(), studioTimezone);

  const {
    data: ninetyPercentFullClasses,
    isLoading: isLoadingNinetyPercentFullClasses,
  } = useGet90PercentFullClasses({
    sessionId,
  });

  const { data: trialStudents, isLoading: isLoadingTrialStudents } =
    useGetAllTrialStudents({
      page: 1,
      limit: 10,
      startDate: formattedFirstDayOfWeek,
      endDate: formattedLastDayOfWeek,
      dateField: 'startTime',
    });

  const { data: trialConversion, isLoading: isLoadingTrialConversion } =
    useGetTrialConversion({
      startDate: formattedFirstDayOfWeek,
      endDate: formattedLastDayOfWeek,
      dateField: 'startTime',
    });

  const {
    data: expiringCards,
    isLoading: isLoadingExpiringCards,
    isError: isExpiringCardsError,
  } = useGetExpiringCards({ activeOnly: true });

  const expiringCardsSafe =
    isExpiringCardsError || !Array.isArray(expiringCards) ? [] : expiringCards;
  const expiringSoonCount = expiringCardsSafe.filter(
    (c) => !c.isExpired,
  ).length;
  const expiredCardsCount = expiringCardsSafe.filter((c) => c.isExpired).length;

  const { hasSuperAdminAccess } = useCurrentUserRole();

  const metrics: Array<MetricItem> = [
    {
      id: 'classes',
      color: 'yellow',
      icon: GraduationCap,
      label: 'Classes ≥ 90% Full',
      description:
        'Active classes that are at or above 90% of their enrollment capacity. These may need attention before they reach max capacity.',
      isLoading: isLoadingNinetyPercentFullClasses,
      mobileValue: ninetyPercentFullClasses?.totalCount ?? 0,
      modalValue: `${ninetyPercentFullClasses?.totalCount ?? 0} classes`,
      desktopLabel: (
        <>{ninetyPercentFullClasses?.totalCount ?? 0} Classes ≥ 90% Full</>
      ),
    },
    {
      id: 'trials',
      color: 'blue',
      icon: Sparkles,
      label: 'Trials This Week',
      description:
        'Trial appointments scheduled during the current studio week. This counts trials occurring this week, including bookings created earlier.',
      isLoading: isLoadingTrialStudents,
      mobileValue: trialStudents?.pagination.totalCount ?? 0,
      modalValue: `${trialStudents?.pagination.totalCount ?? 0} trials this week`,
      desktopLabel: (
        <>
          {trialStudents?.pagination.totalCount ?? 0} Trials This Week
        </>
      ),
    },
    {
      id: 'conversion',
      color: 'emerald',
      icon: TrendingUp,
      label: 'Trial Conversion (This Week)',
      description:
        'Percentage of trial families from this week who enrolled in a paid class. Higher conversion means more trials are turning into enrollments.',
      isLoading: isLoadingTrialConversion,
      mobileValue: formatTrialConversionRate(trialConversion),
      modalValue: formatTrialConversionRate(trialConversion),
      desktopLabel: (
        <>
          <TrialConversionSummary
            metrics={trialConversion}
            isLoading={isLoadingTrialConversion}
            variant='banner'
          />{' '}
          Trial Conversion (This Week)
        </>
      ),
    },
    ...(hasSuperAdminAccess
      ? [
          {
            id: 'cards',
            color: 'red' as const,
            icon: CreditCard,
            label: 'Payment Cards',
            description:
              'Saved payment cards that are expiring soon or have already expired. Families with expiring cards may need updated billing info.',
            isLoading: isLoadingExpiringCards,
            mobileValue: isExpiringCardsError
              ? '—'
              : `${expiringSoonCount}/${expiredCardsCount}`,
            modalValue: isExpiringCardsError ? (
              'Unable to load card data'
            ) : (
              <>
                {expiringSoonCount} expiring soon · {expiredCardsCount} expired
              </>
            ),
            desktopLabel: isExpiringCardsError ? (
              'Error loading cards'
            ) : (
              <>
                {expiringSoonCount} Cards Expiring, {expiredCardsCount} Expired
              </>
            ),
          },
        ]
      : []),
  ];

  const selectedMetric =
    metrics.find((metric) => metric.id === selectedMetricId) ?? null;

  return (
    <>
      <div className='w-full min-w-0 rounded-2xl border-b border-border/30 bg-[#FFF9E6] px-3 py-3 backdrop-blur-xl min-[1100px]:rounded-full min-[1100px]:px-4'>
        {/* Mobile / tablet: icon + value in one row (match nav collapse at 1100px) */}
        <div className='flex w-full items-center justify-between gap-1 min-[1100px]:hidden'>
          {metrics.map((metric) => (
            <CompactMetric
              key={metric.id}
              color={metric.color}
              icon={metric.icon}
              value={metric.mobileValue}
              label={metric.label}
              isLoading={metric.isLoading}
              onClick={() => {
                setSelectedMetricId(metric.id);
              }}
            />
          ))}
        </div>

        {/* Desktop: full label pills */}
        <div className='hidden w-full flex-wrap items-center gap-2 min-[1100px]:flex'>
          {metrics.map((metric) => (
            <DesktopPill
              key={metric.id}
              color={metric.color}
              icon={metric.icon}
              isLoading={metric.isLoading}
            >
              {metric.desktopLabel}
            </DesktopPill>
          ))}
        </div>
      </div>

      <Dialog
        open={selectedMetric != null}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedMetricId(null);
          }
        }}
      >
        {selectedMetric ? (
          <DialogContent className='max-w-sm'>
            <DialogHeader>
              <div className='flex items-center gap-3'>
                <div
                  className={cn(
                    'flex size-10 shrink-0 items-center justify-center rounded-full',
                    bgColorClass[selectedMetric.color],
                  )}
                >
                  <selectedMetric.icon
                    className={cn('size-5', textColorClass[selectedMetric.color])}
                    strokeWidth={2}
                  />
                </div>
                <DialogTitle className='text-left text-base'>
                  {selectedMetric.label}
                </DialogTitle>
              </div>
              <DialogDescription className='text-left'>
                {selectedMetric.description}
              </DialogDescription>
            </DialogHeader>
            <div
              className={cn(
                'rounded-xl border px-4 py-3 text-center',
                bgColorClass[selectedMetric.color],
                borderColorClass[selectedMetric.color],
              )}
            >
              <p className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>
                Current value
              </p>
              <p
                className={cn(
                  'mt-1 text-2xl font-semibold tabular-nums',
                  textColorClass[selectedMetric.color],
                )}
              >
                {selectedMetric.isLoading ? (
                  <Loader2 className='mx-auto size-6 animate-spin' />
                ) : (
                  selectedMetric.modalValue
                )}
              </p>
            </div>
          </DialogContent>
        ) : null}
      </Dialog>
    </>
  );
};
